import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "./RegisterInternship.css";

const API = process.env.REACT_APP_API_URL || "https://porfolio-backend-production-aab2.up.railway.app/api";

const domains = [
  "Full Stack Development",
  "Python Development",
  "Java Development",
  "Web App Building",
  "AI Tools & Automation",
];

const years = ["1st Year", "2nd Year", "3rd Year", "4th Year", "Graduated"];

const EMPTY = {
  fullName: "",
  email: "",
  phone: "",
  collegeName: "",
  department: "",
  yearOfStudy: "",
  domain: "",
  transactionId: "",
  githubProfile: "",
  linkedinProfile: "",
  paymentScreenshot: null,
};

const UPI_ID = "9698637432@fam";
const AMOUNT = 499;
const UPI_LINK = `upi://pay?pa=${UPI_ID}&pn=Ariyom+Tech+Solutions&am=${AMOUNT}&cu=INR&tn=Internship+Fee`;
const QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(UPI_LINK)}`;

export default function RegisterInternship() {
  const location = useLocation();
  const navigate = useNavigate();
  const preSelected = location.state?.domain || "";

  const [form, setForm] = useState({ ...EMPTY, domain: preSelected });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [registeredId, setRegisteredId] = useState("");
  const [copied, setCopied] = useState(false);

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const setFile = (e) =>
    setForm((f) => ({ ...f, paymentScreenshot: e.target.files[0] }));

  function copyUPI() {
    navigator.clipboard.writeText(UPI_ID);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function validate() {
    const err = {};
    if (!form.fullName.trim()) err.fullName = "Required";
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) err.email = "Valid email required";
    if (!form.phone.match(/^[6-9]\d{9}$/)) err.phone = "Valid 10-digit mobile required";
    if (!form.collegeName.trim()) err.collegeName = "Required";
    if (!form.department.trim()) err.department = "Required";
    if (!form.yearOfStudy) err.yearOfStudy = "Required";
    if (!form.domain) err.domain = "Select a domain";
    if (!form.transactionId.trim()) err.transactionId = "Required";
    return err;
  }

  async function handleSubmit() {
    const err = validate();
    if (Object.keys(err).length) { setErrors(err); return; }
    setErrors({});
    setLoading(true);

    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v) data.append(k, v);
      });

      const res = await axios.post(
        `${API}/internships/register`,
        data,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setRegisteredId(res.data.certificateId || "");
      setSubmitted(true);
    } catch (err) {
      alert(err.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="reg-page">
        <div className="reg-success-card">
          <div className="reg-success-icon">✅</div>
          <h2>Registration Successful!</h2>
          <p>
            Thank you, <strong>{form.fullName}</strong>. Your application has been received.
          </p>
          {registeredId && (
            <div className="reg-id-box">
              Your Certificate ID: <strong>{registeredId}</strong>
            </div>
          )}
          <p className="reg-success-note">
            We'll verify your payment and send your onboarding details to{" "}
            <strong>{form.email}</strong> within 24 hours.
          </p>
          <div className="reg-success-actions">
            <button className="reg-btn-primary" onClick={() => navigate("/internship")}>
              Back to Internship
            </button>
            <button className="reg-btn-outline" onClick={() => navigate("/verify")}>
              Verify Certificate
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reg-page">
      <div className="reg-container">
        <div className="reg-header">
          <span className="reg-badge">30-Day Internship</span>
          <h1>Register for Internship</h1>
          <p>Conducted by Ariyom Tech Solutions · Registered under Udyam/MSME</p>
        </div>

        <div className="reg-form">

          {/* Personal Info */}
          <div className="reg-group-label">Personal Information</div>
          <div className="reg-row">
            <Field label="Full Name *" error={errors.fullName}>
              <input type="text" placeholder="Rahul Kumar" value={form.fullName} onChange={set("fullName")} />
            </Field>
            <Field label="Email *" error={errors.email}>
              <input type="email" placeholder="rahul@email.com" value={form.email} onChange={set("email")} />
            </Field>
          </div>

          <div className="reg-row">
            <Field label="Phone Number *" error={errors.phone}>
              <input type="tel" placeholder="9876543210" value={form.phone} onChange={set("phone")} maxLength={10} />
            </Field>
            <Field label="GitHub Profile" error={errors.githubProfile}>
              <input type="url" placeholder="https://github.com/username" value={form.githubProfile} onChange={set("githubProfile")} />
            </Field>
          </div>

          <Field label="LinkedIn Profile" error={errors.linkedinProfile}>
            <input type="url" placeholder="https://linkedin.com/in/username" value={form.linkedinProfile} onChange={set("linkedinProfile")} />
          </Field>

          {/* Academic */}
          <div className="reg-group-label">Academic Details</div>
          <div className="reg-row">
            <Field label="College Name *" error={errors.collegeName}>
              <input type="text" placeholder="ABC Engineering College" value={form.collegeName} onChange={set("collegeName")} />
            </Field>
            <Field label="Degree / Department *" error={errors.department}>
              <input type="text" placeholder="B.E. Computer Science" value={form.department} onChange={set("department")} />
            </Field>
          </div>

          <Field label="Year of Study *" error={errors.yearOfStudy}>
            <select value={form.yearOfStudy} onChange={set("yearOfStudy")}>
              <option value="">Select year</option>
              {years.map((y) => (<option key={y}>{y}</option>))}
            </select>
          </Field>

          {/* Domain */}
          <div className="reg-group-label">Internship Domain</div>
          <Field label="Select Domain *" error={errors.domain}>
            <select value={form.domain} onChange={set("domain")}>
              <option value="">Select a domain</option>
              {domains.map((d) => (<option key={d}>{d}</option>))}
            </select>
          </Field>

          {/* Payment */}
          <div className="reg-group-label">Payment — ₹499</div>

          <div className="reg-payment-box">
            <div className="reg-payment-left">
              <p className="reg-payment-title">📱 Scan & Pay ₹499</p>
              <img src={QR_URL} alt="UPI QR Code" className="reg-qr-img" />
              <p className="reg-payment-note">GPay · PhonePe · Paytm · Any UPI app</p>
            </div>

            <div className="reg-payment-divider"><span>or</span></div>

            <div className="reg-payment-right">
              <p className="reg-payment-title">💳 Pay via UPI ID</p>
              <div className="reg-upi-box">
                <span className="reg-upi-id">{UPI_ID}</span>
                <button type="button" className="reg-copy-btn" onClick={copyUPI}>
                  {copied ? "✓ Copied" : "Copy"}
                </button>
              </div>
              <a href={UPI_LINK} className="reg-pay-btn">Open UPI App →</a>
              <p className="reg-payment-note">
                After paying, enter the Transaction ID below and upload your payment screenshot.
              </p>
            </div>
          </div>

          <div className="reg-row">
            <Field label="Transaction ID *" error={errors.transactionId}>
              <input type="text" placeholder="Enter UPI Transaction ID" value={form.transactionId} onChange={set("transactionId")} />
            </Field>
            <Field label="Payment Screenshot" error={errors.paymentScreenshot}>
              <input type="file" accept="image/*" onChange={setFile} className="reg-file-input" />
            </Field>
          </div>

          <button className="reg-submit-btn" onClick={handleSubmit} disabled={loading}>
            {loading ? "Submitting..." : "Submit Registration →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div className={`reg-field${error ? " reg-field--error" : ""}`}>
      <label>{label}</label>
      {children}
      {error && <span className="reg-error-msg">{error}</span>}
    </div>
  );
}